/**
 * Image compression utility for blogs and rich text content.
 * Automatically resizes large images and optimizes quality
 * to ensure payloads stay small (< 250KB per image) and fast to transmit.
 */

/**
 * Compresses a File, Blob, or Base64 Data URL.
 * @param {File|Blob|string} fileOrDataUrl - The image to compress
 * @param {number} maxWidth - Maximum width (default: 1200)
 * @param {number} maxHeight - Maximum height (default: 1200)
 * @param {number} quality - Compression quality 0-1 (default: 0.8)
 * @returns {Promise<string>} Base64 Data URL of compressed image
 */
export const compressImageFileOrDataUrl = (fileOrDataUrl, maxWidth = 1200, maxHeight = 1200, quality = 0.8) => {
    return new Promise((resolve) => {
        if (!fileOrDataUrl) return resolve(fileOrDataUrl);

        // If it's a web URL (http:// or https://), no need to compress
        if (typeof fileOrDataUrl === 'string' && /^https?:\/\//i.test(fileOrDataUrl)) {
            return resolve(fileOrDataUrl);
        }

        // If it's a small base64 string (< 100KB), keep it as is
        if (typeof fileOrDataUrl === 'string' && fileOrDataUrl.startsWith('data:image/') && fileOrDataUrl.length < 130000) {
            return resolve(fileOrDataUrl);
        }

        const img = new Image();

        const processImage = () => {
            try {
                let { naturalWidth: width, naturalHeight: height } = img;
                if (!width || !height) {
                    width = img.width || 800;
                    height = img.height || 600;
                }

                // Downscale dimensions if larger than maxWidth or maxHeight
                if (width > maxWidth || height > maxHeight) {
                    const ratio = Math.min(maxWidth / width, maxHeight / height);
                    width = Math.round(width * ratio);
                    height = Math.round(height * ratio);
                }

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');

                if (!ctx) {
                    return resolve(typeof fileOrDataUrl === 'string' ? fileOrDataUrl : '');
                }

                // Draw image on canvas
                ctx.drawImage(img, 0, 0, width, height);

                // Try WebP first for best compression & transparency support
                let compressed = '';
                try {
                    compressed = canvas.toDataURL('image/webp', quality);
                    if (!compressed.startsWith('data:image/webp')) {
                        // If browser doesn't support webp export, fall back to jpeg
                        compressed = canvas.toDataURL('image/jpeg', quality);
                    }
                } catch {
                    compressed = canvas.toDataURL('image/jpeg', quality);
                }

                // If compression somehow produced a larger string than original, keep original
                if (typeof fileOrDataUrl === 'string' && fileOrDataUrl.length < compressed.length) {
                    resolve(fileOrDataUrl);
                } else {
                    resolve(compressed);
                }
            } catch (err) {
                console.warn('Image compression failed, using original:', err);
                resolve(typeof fileOrDataUrl === 'string' ? fileOrDataUrl : '');
            }
        };

        img.onload = () => {
            processImage();
        };

        img.onerror = () => {
            console.warn('Could not load image for compression');
            if (typeof fileOrDataUrl === 'string') {
                resolve(fileOrDataUrl);
            } else {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = () => resolve('');
                reader.readAsDataURL(fileOrDataUrl);
            }
        };

        if (typeof fileOrDataUrl === 'string') {
            img.src = fileOrDataUrl;
        } else if (fileOrDataUrl instanceof Blob || fileOrDataUrl instanceof File) {
            const reader = new FileReader();
            reader.onload = () => {
                img.src = reader.result;
            };
            reader.onerror = () => resolve('');
            reader.readAsDataURL(fileOrDataUrl);
        } else {
            resolve(fileOrDataUrl);
        }
    });
};

/**
 * Inspects all <img> tags inside an HTML string.
 * If any img src is a large Base64 data URL, it compresses it.
 * @param {string} html - The HTML content
 * @returns {Promise<string>} HTML with compressed images
 */
export const compressBlogContentImages = async (html) => {
    if (!html || typeof html !== 'string') return html;

    // Fast check: if no data:image/ exists in the HTML, return as is
    if (!html.includes('data:image/')) {
        return html;
    }

    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const images = doc.querySelectorAll('img');

        if (images.length === 0) return html;

        let modified = false;

        for (const img of Array.from(images)) {
            const src = img.getAttribute('src');
            if (src && src.startsWith('data:image/') && src.length > 130000) {
                // Compress if > ~100KB
                const compressed = await compressImageFileOrDataUrl(src, 1200, 1200, 0.8);
                if (compressed && compressed.length < src.length) {
                    img.setAttribute('src', compressed);
                    modified = true;
                }
            }
        }

        if (modified) {
            return doc.body.innerHTML;
        }
        return html;
    } catch (err) {
        console.warn('Failed to parse and compress content images:', err);
        return html;
    }
};
